import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { MessagesSquare, PenSquare, RefreshCw, Search, X } from "lucide-react";
import { useFacility } from "../../context/FacilityContext";
import { deleteOlliChat, getOlliChat, listOlliChats, olliErrorOf, sendToOlli } from "../lib/olli";
import { ParticleOrb } from "../components/marketing/ParticleOrb";
import { VoiceButton, VoiceStrip, useVoiceInput } from "../components/VoiceInput";
import { OlliThreadView, ThreadHistory } from "../components/assistant/OlliThreadView";
import { OlliSendButton } from "../components/assistant/OlliSendButton";
import { PageLoader } from "../components/PageLoader";

/**
 * Ask Olli (design: screens/AskOlli.tsx), on the Olli assistant API (item 1),
 * the same one the floating assistant uses (lib/olli.js):
 *   GET    /api/olli/chats          your chats
 *   GET    /api/olli/chats/:id      a chat's messages
 *   POST   /api/olli/chats/messages { chat_id?, message, page_path, facility_id? }
 *   DELETE /api/olli/chats/:id      delete a chat
 *
 * Olli answers read-only lookups on the clinic's own data — leads,
 * appointments, patients, tasks, WhatsApp, campaigns, support, ads, AI agents,
 * the plan and wallet — for the selected location (several lookups for one
 * question), searches the knowledge base, and can fill in a new lead. An
 * answer shows its facts, the documents it used, links to the records and
 * screens it came from, and follow-up chips; a lead it understood opens the
 * lead form pre-filled ("Filled by Olli"), and nothing is saved until the
 * person saves it. A turn that fails shows why with Retry; the backend saves
 * nothing for it. `?chat=<id>` opens one chat (Recent prompts, the floating
 * assistant's "Open in Ask Olli"), and the open chat stays in the URL.
 */

// Questions the backend's fixed lookups answer (services/olliAssistant.js).
const STARTERS = [
  "How many leads came in this week, and how many booked?",
  "Who's coming in tomorrow?",
  "What's waiting in support?",
  "How did our WhatsApp messages do this week?",
  "What plan am I on?",
  "Add a lead: Priya Sharma, 9876543210, implants",
];

const titleFrom = (prompt) => {
  const t = prompt.trim().replace(/\s+/g, " ");
  return t.length > 54 ? `${t.slice(0, 52).trimEnd()}…` : t || "New chat";
};

/** "Dr. Ananya" from "Dr. Ananya Rao", "Ananya" from "Ananya Rao". */
function greetingName() {
  try {
    const name = String(JSON.parse(localStorage.getItem("user") || "null")?.name || "").trim();
    const words = name.split(/\s+/).filter(Boolean);
    if (!words.length) return "";
    return /^dr\.?$/i.test(words[0]) && words[1] ? `${words[0]} ${words[1]}` : words[0];
  } catch (_) {
    return "";
  }
}

// A reply with no text (shouldn't happen) still says something plain.
const CANT_ANSWER = "Olli couldn't answer that. Try asking about leads, appointments, WhatsApp, campaigns or support.";

export default function AskOlli() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatParam = Number(searchParams.get("chat")) || null;
  const fac = useFacility();
  // Answers cover the selected location; the backend checks the id is this clinic's.
  const facilityId = fac?.apiParams?.().facility_id || null;
  const [conversations, setConversations] = useState([]);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const [conversationsError, setConversationsError] = useState("");
  // The chat being opened: its thread shows the loader (not the previous chat) until
  // it arrives, and an answer for a chat no longer being opened is dropped.
  const [opening, setOpening] = useState(null);
  const openingRef = useRef(null);
  const [activeId, setActiveIdState] = useState(null);
  // Read by the ?chat= effect, which must not reopen the chat that is already open.
  const activeRef = useRef(null);
  const setActiveId = (id) => { activeRef.current = id; setActiveIdState(id); };
  // The open chat in the URL (replace: the back button leaves Ask Olli, not each chat).
  const setChatParam = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set("chat", String(id));
      else next.delete("chat");
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [messages, setMessages] = useState([]);
  const [pendingTitle, setPendingTitle] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [chatsOpen, setChatsOpen] = useState(false);
  const textareaRef = useRef(null);
  const bottomRef = useRef(null);
  const handled = useRef(false);
  const voice = useVoiceInput({ onTranscript: (spoken) => setText(spoken) });
  const name = greetingName();
  const active = conversations.find((c) => c.id === activeId);
  const hasThread = messages.length > 0;
  const threadTitle = active?.title || pendingTitle || "Ask Olli";
  const listening = voice.status === "listening" || voice.status === "starting";
  const orbState = sending ? "thinking" : listening ? "listening" : "idle";

  const fetchConversations = useCallback(async () => {
    try {
      const chats = await listOlliChats();
      setConversations(chats.map((c) => ({ ...c, title: c.title || "New chat" })));
      setConversationsError("");
    } catch (err) {
      setConversationsError(err.response?.data?.message || "Your chats couldn't be loaded.");
    }
    setConversationsLoaded(true);
  }, []);
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const send = useCallback(
    async (raw) => {
      const prompt = raw.trim();
      if (!prompt || sending) return;
      const temp = { id: `temp-${Date.now()}`, role: "user", text: prompt };
      if (!activeId) setPendingTitle(titleFrom(prompt));
      setMessages((prev) => [...prev, temp]);
      setText("");
      setError("");
      setSending(true);
      try {
        const { chatId, reply } = await sendToOlli({ message: prompt, chatId: activeId, pagePath: location.pathname, facilityId });
        if (!activeId && chatId) { setActiveId(chatId); setChatParam(chatId); }
        setMessages((prev) => [...prev, { ...reply, text: reply.text || CANT_ANSWER }]);
        fetchConversations();
      } catch (err) {
        // The question stays, with why it failed and Retry; nothing of it was saved.
        // A chat deleted elsewhere comes back 404: the retry starts a fresh one.
        if (err.response?.status === 404 && activeId) { setActiveId(null); setChatParam(null); }
        setMessages((prev) => [...prev, { id: `error-${Date.now()}`, role: "olli", text: "", error: olliErrorOf(err), retry: prompt }]);
      } finally {
        setSending(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setActiveId only writes state and a ref
    [activeId, sending, fetchConversations, facilityId, location.pathname, setChatParam],
  );
  // Retry: drop the failed question and its error, then ask again.
  const retry = (failed) => {
    setMessages((prev) => {
      const at = prev.findIndex((m) => m.id === failed.id);
      return at > 0 && prev[at - 1].role === "user" ? [...prev.slice(0, at - 1), ...prev.slice(at + 1)] : prev.filter((m) => m.id !== failed.id);
    });
    send(failed.retry);
  };

  const openThread = async (id) => {
    setChatsOpen(false);
    setError("");
    openingRef.current = id;
    setOpening(id);
    setChatParam(id);
    try {
      const thread = await getOlliChat(id);
      if (openingRef.current !== id) return;
      setActiveId(id);
      setPendingTitle("");
      setMessages(thread.map((m) => (m.role === "olli" && !m.text ? { ...m, text: CANT_ANSWER } : m)));
    } catch (err) {
      if (openingRef.current !== id) return;
      setError(err.response?.status === 404 ? "That chat no longer exists." : err.response?.data?.message || "Failed to load this chat.");
      setChatParam(activeRef.current);
    }
    openingRef.current = null;
    setOpening(null);
  };

  const newChat = () => {
    openingRef.current = null;
    setOpening(null);
    setActiveId(null);
    setMessages([]);
    setPendingTitle("");
    setText("");
    setError("");
    setChatsOpen(false);
    setChatParam(null);
    textareaRef.current?.focus();
  };

  // ThreadHistory asks "Delete chat?" on the row before calling this.
  const deleteThread = async (id) => {
    try {
      await deleteOlliChat(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) newChat();
    } catch (err) {
      setError(err.response?.data?.message || "That chat couldn't be deleted.");
    }
  };

  // A prompt handed over from elsewhere in the app (navigate(..., { state: { prompt } })).
  useEffect(() => {
    const incoming = location.state ?? null;
    if (handled.current || !incoming) return;
    handled.current = true;
    if (incoming.threadId) openThread(incoming.threadId);
    else if (incoming.prompt) send(incoming.prompt);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once for the handed-over prompt, like the design
  }, []);
  // ?chat=<id>: open that chat (on arrival, and when a link changes it while here).
  useEffect(() => {
    if (!chatParam || chatParam === activeRef.current || chatParam === openingRef.current) return;
    openThread(chatParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to the URL only
  }, [chatParam]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, sending]);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  const submit = (event) => {
    event.preventDefault();
    send(text);
  };
  const threads = conversations.filter((item) => item.title.toLowerCase().includes(filter.trim().toLowerCase()));

  const composer = (
    <form onSubmit={submit} className="w-full rounded-lg bg-paper p-2.5 shadow-lift ring-1 ring-line focus-within:ring-sky-deep/50">
      <label htmlFor="ask-olli-input" className="sr-only">Message Olli</label>
      <textarea
        id="ask-olli-input"
        ref={textareaRef}
        autoFocus
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            send(text);
          }
        }}
        readOnly={listening}
        rows={hasThread ? 1 : 2}
        maxLength={2000}
        placeholder={hasThread ? "Reply to Olli" : "Ask about your leads, appointments, patients, ads or plan, or add a lead"}
        className="max-h-[200px] w-full resize-none bg-transparent px-3 py-2 text-body text-ink outline-none placeholder:text-muted"
      />
      {voice.status !== "idle" && <VoiceStrip status={voice.status} levels={voice.levels} seconds={voice.seconds} notice={voice.notice} onStop={voice.stop} />}
      {voice.error && <p role="status" className="px-3 pt-1 text-caption text-muted">{voice.error}</p>}
      {/* Not in the design: the server's reason when a message can't be sent or a chat can't be opened. */}
      {error && <p role="alert" className="px-3 pt-1 text-caption text-danger">{error}</p>}
      <div className="flex items-center gap-2 pt-1">
        <VoiceButton status={voice.status} onStart={() => { voice.start(); }} onStop={voice.stop} />
        <OlliSendButton sending={sending} disabled={!text.trim() || listening} className="ml-auto" aria-label={sending ? "Sending" : "Send to Olli"} />
      </div>
    </form>
  );

  return (
    <main className="relative flex h-[calc(100vh-80px)] min-w-0 text-ink max-sm:h-[calc(100dvh-112px)]">
      <aside className={`${chatsOpen ? "flex" : "hidden"} absolute inset-y-0 left-0 z-30 w-[288px] flex-col border-r border-line bg-paper p-3 md:relative md:flex md:bg-paper/60`} aria-label="Your chats">
        <div className="flex items-center gap-2 pb-2">
          <button type="button" onClick={newChat} className="flex h-11 flex-1 items-center gap-2 rounded-full px-3 text-small font-semibold text-ink hover:bg-mist"><PenSquare size={18} strokeWidth={1.75} />New chat</button>
          <button type="button" aria-label="Close chats" onClick={() => setChatsOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full text-muted hover:bg-mist md:hidden"><X size={18} strokeWidth={1.75} /></button>
        </div>
        <label className="flex h-10 items-center gap-2 rounded-full bg-mist px-3 text-small text-muted focus-within:ring-2 focus-within:ring-sky-deep/40">
          <Search size={16} strokeWidth={1.75} />
          <span className="sr-only">Search chats</span>
          <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search chats" className="min-w-0 flex-1 bg-transparent text-ink outline-none placeholder:text-muted" />
        </label>
        <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1 [scrollbar-width:thin]">
          {conversationsError && <div role="alert" className="mb-3 flex items-center justify-between gap-3 rounded-lg bg-danger-soft px-3 py-2 text-caption text-danger"><span>{conversationsError}</span><button type="button" onClick={fetchConversations} className="inline-flex shrink-0 items-center gap-1 font-semibold text-ink hover:underline"><RefreshCw size={13} strokeWidth={1.75} />Try again</button></div>}
          {!conversationsLoaded ? <PageLoader label="Loading chats…" className="py-10" /> : conversationsError && !conversations.length ? null : threads.length ? <ThreadHistory threads={threads} onSelect={openThread} onDelete={deleteThread} /> : <p className="px-2 py-6 text-small text-muted">{filter ? "No chats match your search." : "Your chats will appear here."}</p>}
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center gap-3 px-4 sm:px-6">
          <button type="button" onClick={() => setChatsOpen(true)} className="flex h-10 shrink-0 items-center gap-2 rounded-full px-3 text-small font-medium text-muted hover:bg-paper md:hidden"><MessagesSquare size={18} strokeWidth={1.75} />Chats</button>
          {hasThread && <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-visible" aria-hidden="true"><ParticleOrb size={44} state={orbState} /></span>}<p className="min-w-0 truncate text-body font-semibold">{hasThread ? threadTitle : "Ask Olli"}</p>
          {hasThread && <button type="button" onClick={newChat} className="ml-auto flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-3 text-small font-medium text-ink hover:bg-paper"><PenSquare size={16} strokeWidth={1.75} />New chat</button>}
        </header>

        {opening ? (
          <PageLoader label="Loading chat…" className="min-h-0 flex-1" />
        ) : hasThread ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-6">
              <div className="mx-auto w-full max-w-3xl py-6">
                <OlliThreadView messages={messages} typing={sending} onChip={send} onRetry={retry} />
                <div ref={bottomRef} />
              </div>
            </div>
            <div className="shrink-0 px-4 pb-4 sm:px-6">
              <div className="mx-auto w-full max-w-3xl">{composer}<p className="pt-2 text-center text-caption text-muted">Olli reads your clinic's data and documents and can fill in a new lead. Nothing is saved, sent or paid until you do it.</p></div>
            </div>
          </>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto px-4 pb-16 sm:px-6">
            <div className="animate-fade-in flex w-full max-w-3xl flex-col items-center">
              <div className="-my-6 flex h-[260px] w-full justify-center overflow-visible" aria-hidden="true"><ParticleOrb size={260} state={orbState} /></div>
              <h1 className="mt-2 text-center text-h1 font-semibold">{name ? `What should Olli do, ${name}?` : "What should Olli do?"}</h1>
              <p className="mt-2 text-center text-small text-muted">{fac?.currentFacility ? `Ask about ${fac.currentFacility.name}: leads, appointments, patients, WhatsApp, support, ads, your plan and documents, or add a lead.` : "Ask about your leads, appointments, patients, WhatsApp, support, ads, your plan and documents, or add a lead."}</p>
              <div className="mt-8 w-full">{composer}</div>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {STARTERS.map((item) => <button key={item} type="button" onClick={() => send(item)} className="rounded-full bg-paper px-3.5 py-2 text-caption text-ink ring-1 ring-line hover:bg-sky-soft">{item}</button>)}
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}
